import React, { useEffect, memo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlaskit/theme/constants';
import { SpotlightTarget, SpotlightTransition, SpotlightManager } from '@atlaskit/onboarding';
import { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import Tooltip from '@atlaskit/tooltip';
import { AnimateIn } from '@atlaskit/motion';

import type { ObjectSidebarEditorAIAction } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { useReactProps_DO_NOT_USE } from '@atlassian/embedded-confluence/useReactProps';
import type { IframePassThroughProps } from '@atlassian/embedded-confluence/IframeElementType';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';

import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { AISmartButtonWrapper } from '@confluence/ai-floating-context-menu/entry-points/AISmartButtonWrapper';
import { AISmartButtonStateHandler } from '@confluence/ai-floating-context-menu/entry-points/AISmartButtonStateHandler';
import { AIFloatingContextMenu } from '@confluence/ai-floating-context-menu/entry-points/AIFloatingContextMenu';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { fg } from '@confluence/feature-gating';
import { PageHeaderLoomEntryPoint } from '@confluence/page-header-loom-button';
import { useCollabNetworkStatus } from '@confluence/collab-network-status';
import { expVal } from '@confluence/feature-experiments';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import {
	useSidebarControlState,
	useSidebarControlExpandCollapsedState,
	useObjectSidebarActions,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar';
import { useIsLoomDisabled } from '@confluence/loom-utils/entry-points/useIsLoomDisabled';
import { LoadableLazy } from '@confluence/loadable';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import { usePreviewPanelsState } from '@confluence/preview-panel-api/entry-points/usePreviewPanelsState';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd';
import { OBJECT_SIDEBAR_SPOTLIGHT_TARGET } from '@confluence/modernize-changeboarding/entry-points/spotlightTargets';
import { useAIRovoButtonEnablement } from '@confluence/ai-floating-context-menu/entry-points/useAIRovoButtonEnablement';
import { useIsMobileApp } from '@confluence/mobile-app-detection/entry-points/MobileAppDetection';
import { expValEquals } from '@confluence/feature-experiments';

import { OBJECT_SIDEBAR_TOUR_TARGETS } from './objectSidebarTourTargets';
import { AudioButton } from './sidebar-buttons/AudioButton';
import { CommentPanelButton } from './sidebar-buttons/CommentPanelButton';
import { DetailsPanelButton } from './sidebar-buttons/DetailsPanelButton';
import { EditorAIActions } from './sidebar-buttons/EditorAIActions';
import { UndoButton, RedoButton } from './sidebar-buttons/UndoRedoButton';
import { ShowDiffButton } from './sidebar-buttons/ShowDiffButton';
import { FeedbackCollectorButton } from './sidebar-buttons/FeedbackCollectorButton';
import { OBJECT_SIDEBAR_CONTROL_METRIC } from './perf.config';
import { AudioListenButtonWrapper } from './sidebar-buttons/AudioListenButtonWrapper';

const ObjectSidebarSpotlightTour = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ObjectSidebarSpotlightTour" */ './ObjectSidebarSpotlightTour'
			)
		).ObjectSidebarSpotlightTour,
});

const objectSidebarStyles = css({
	position: 'fixed',
	right: 0,
	bottom: 0,
	zIndex: layers.dialog(),
	pointerEvents: 'none',
	// The selector below ensures the elements behind the empty space taken up by the container remain clickable/responsive to mouse events
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	'& > *': {
		pointerEvents: 'auto',
	},
});

const objectSidebarPaddingStyles = css({
	paddingRight: `${token('space.300')}`,
	paddingBottom: `${token('space.100')}`,
});

const databaseObjectSidebarPaddingStyles = css({
	paddingRight: `${token('space.100')}`,
	paddingBottom: `${token('space.100')}`,
});

const largeBottomPaddingStyles = css({
	paddingBottom: `${token('space.300')}`,
});

const viewModeStylesWithLegacyFooter = css({
	//z index is set to 1 to ensure that the object sidebar is below the inline comment component
	zIndex: 1,
});

const viewModeStylesWithNewFooter = css({
	//z index is set to 2 to ensure that the object sidebar is below the inline comment component but above the "Add a comment" button
	zIndex: 2,
});

const miniModalModeStyles = css({
	//z index is set to modal to ensure that the mini modal renders above everything else as expected
	zIndex: layers.modal(),
});

const collapsibleObjectSidebarContainerStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: 'radius.full',
});

const collapsibleSquircleObjectSidebarContainerStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: 'radius.xlarge',
});

const collapsibleContainerStyles = css({
	width: `${token('space.600')}`,
	marginLeft: 'auto',
});

const collapsibleBottomSidebarStyles = xcss({
	paddingTop: 'space.100',
	marginBottom: 'space.050',
});

const rovoButtonCollapsibleBottomSidebarStyles = css({
	paddingTop: token('space.100'),
	marginBottom: token('space.200'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '1px',
});

const collapsibleToggleStyles = css({
	margin: 'auto',
	width: `${token('space.400')}`,
});

const dividerStyle = xcss({
	borderBottom: `${token('border.width.selected')} solid ${token('color.border')}`,
	display: 'inline-block',
	width: '16px',
	height: '1px',
});

const hiddenStyles = css({
	display: 'none',
});

const i18n = defineMessages({
	expandToggleLabel: {
		id: 'object-sidebar-components.expand-toggle-label',
		defaultMessage: 'Expand action bar',
		description: 'Label for button to expand the floating action bar',
	},
	collapseToggleLabel: {
		id: 'object-sidebar-components.collapse-toggle-label',
		defaultMessage: 'Collapse action bar',
		description: 'Label for button to collapse the floating action bar',
	},
});

export type ObjectSidebarControlProps = {
	hasCommentsButton?: boolean;
	hasDetailsButton?: boolean;
	hasLoomButton?: boolean;
	hasAiButton?: boolean;
	hasUndoRedoButton?: boolean;
	editorAIActions?: ObjectSidebarEditorAIAction[];
	undoRedoButtonActions?: {
		canUndo?: boolean;
		canRedo?: boolean;
		undo?: () => boolean;
		redo?: () => boolean;
	};
	showDiffButtonActions?: {
		isShowDiffSelected: boolean;
		isShowDiffEnabled: boolean;
		onShowDiff: () => void;
	};
	hasAudioButton?: boolean;
	hasFeedbackButton?: boolean;
	contentId: string;
	spaceKey?: string;
	contentType?: string | null;
	contentTitle?: string | null;
	lastModifiedDate?: string | null;
	pageCommentsSectionInView?: boolean;
	aiIsStreaming?: boolean;
	isUnpublishedDraft?: boolean;
	largeBottomPadding?: boolean;
	shouldDisableUndoRedo?: boolean;
	isDatabasesStagingArea?: boolean;
};

export const ObjectSidebarControl = memo(
	({
		hasCommentsButton,
		hasDetailsButton,
		hasLoomButton,
		hasAiButton,
		undoRedoButtonActions,
		hasUndoRedoButton,
		showDiffButtonActions,
		hasAudioButton,
		hasFeedbackButton = true,
		contentId,
		spaceKey,
		contentType,
		contentTitle,
		lastModifiedDate,
		pageCommentsSectionInView,
		aiIsStreaming = false,
		isUnpublishedDraft,
		largeBottomPadding,
		editorAIActions,
		shouldDisableUndoRedo = false,
		isDatabasesStagingArea = false,
	}: ObjectSidebarControlProps) => {
		const [{ isCollabOffline }] = useCollabNetworkStatus();

		// Network status is used when:
		// - isCollabOffline: The collab provider has detected a network disconnection
		// - platform_editor_offline_editing_web: Parent experiment for offline editing experience (must be enabled)
		// - platform_editor_offline_editing_wrapper_override: Kill switch to disable this feature (must be disabled)
		const isNetworkDisabled =
			isCollabOffline &&
			expVal('platform_editor_offline_editing_web', 'isEnabled', false) &&
			!fg('platform_editor_offline_editing_wrapper_override');
		const { isFullScreenEmbedView } = useReactProps_DO_NOT_USE<IframePassThroughProps>() || {};

		const { formatMessage } = useIntl();
		const [actionBarIsHovered, setActionBarIsHovered] = useState(false);
		const sidebarControlState = useSidebarControlState();
		const sidebarControlExpandCollapsedState = useSidebarControlExpandCollapsedState();
		const {
			setSidebarControlState,
			toggleObjectSidebarControl,
			setSidebarControlExpandCollapsedState,
		} = useObjectSidebarActions();

		const pageMode = useGetPageMode();
		const isLoomDisabled = useIsLoomDisabled();

		const rightSidebarContext = useRightSidebarContext();
		const { width: globalSidebarWidth } = rightSidebarContext.getCurrent() || {};
		const { width: previewPanelWidth } = usePreviewPanelsState();

		const { mode: rovoExperienceMode } = useRovoExperienceAPIStore();
		const isRovoButtonEnabled = useAIRovoButtonEnablement();
		const { isMobileApp } = useIsMobileApp();

		const isOnPDFExportPageRouteArgs = {
			selector: (routeName: string | undefined) => {
				if (!routeName) {
					return false;
				}
				return routeName === PDF_EXPORT_PAGE.name;
			},
		};

		const isOnPDFExportPageRoute = useRouteName(isOnPDFExportPageRouteArgs);

		const isLivePage = useIsCurrentPageLive();
		const [{ isViewMode: isLiveViewMode }] = useLivePageMode();
		const renderServerPlaceholder = useRenderServerPlaceholder();

		// whenever the contentID changes we needed to reset the action bar to be expanded
		useEffect(() => {
			setSidebarControlExpandCollapsedState('expanded');
		}, [contentId, setSidebarControlExpandCollapsedState]);

		useEffect(() => {
			setSidebarControlState('shown');
			setSidebarControlExpandCollapsedState('expanded');
			return () => {
				setSidebarControlExpandCollapsedState('collapsed');
			};
		}, [setSidebarControlState, setSidebarControlExpandCollapsedState]);

		const sidebarIsExpanded =
			sidebarControlExpandCollapsedState === 'expanded' || renderServerPlaceholder;

		// Collapsed actions are rendered when collapsed. These can be used e.g. to enable keyboard shortcuts while collapsed.
		const collapsedActions = [];
		const primaryActions: JSX.Element[] = [];

		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			hasUndoRedoButton &&
			undoRedoButtonActions &&
			!isLiveViewMode &&
			fg('platform_editor_ai_aifc_streaming') &&
			(!fg('platform_editor_toolbar_aifc_undo_redo_confluence') || isDatabasesStagingArea)
		) {
			const { canUndo, undo, canRedo, redo } = undoRedoButtonActions;

			if (undo && canUndo !== undefined) {
				primaryActions.push(
					<UndoButton
						canUndo={canUndo}
						undo={undo}
						isDisabled={aiIsStreaming || shouldDisableUndoRedo}
					/>,
				);
			}

			if (redo && canRedo !== undefined) {
				primaryActions.push(
					<RedoButton
						canRedo={canRedo}
						redo={redo}
						isDisabled={aiIsStreaming || shouldDisableUndoRedo}
					/>,
				);
			}
		}
		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			showDiffButtonActions &&
			!isLiveViewMode &&
			fg('platform_editor_ai_aifc_streaming') &&
			(!fg('platform_editor_toolbar_aifc_undo_redo_confluence') || isDatabasesStagingArea)
		) {
			const { onShowDiff, isShowDiffEnabled, isShowDiffSelected } = showDiffButtonActions;

			primaryActions.push(
				<ShowDiffButton
					isShowDiffSelected={isShowDiffSelected}
					isShowDiffEnabled={isShowDiffEnabled && !aiIsStreaming}
					onShowDiff={onShowDiff}
				/>,
			);
		}

		const secondaryActions: JSX.Element[] = [];
		const secondaryActionsTeam25: JSX.Element[] = [];

		if (fg('platform_editor_ai_aifc_streaming')) {
			if (hasCommentsButton && fg('confluence-frontend-comments-panel')) {
				secondaryActionsTeam25.push(
					<CommentPanelButton contentId={contentId} isDisabled={isNetworkDisabled} />,
				);
				if (!sidebarIsExpanded) {
					collapsedActions.push(
						<CommentPanelButton contentId={contentId} collapsed isDisabled={isNetworkDisabled} />,
					);
				}
			}
			if (hasDetailsButton) {
				secondaryActionsTeam25.push(
					<DetailsPanelButton contentId={contentId} isDisabled={isNetworkDisabled} />,
				);
				if (!sidebarIsExpanded) {
					collapsedActions.push(
						<DetailsPanelButton contentId={contentId} collapsed isDisabled={isNetworkDisabled} />,
					);
				}
			}
			if (hasAudioButton) {
				if (fg('confluence_audio_briefings_v3')) {
					secondaryActionsTeam25.push(
						<AudioListenButtonWrapper
							isRovoButtonEnabled={isRovoButtonEnabled}
							contentId={contentId}
							contentType={contentType ?? ''}
							spaceKey={spaceKey ?? ''}
							title={contentTitle ?? ''}
						/>,
					);
				} else {
					secondaryActionsTeam25.push(
						<AudioButton
							contentId={contentId}
							contentType={contentType}
							spaceKey={spaceKey}
							contentTitle={contentTitle}
							isDisabled={isNetworkDisabled}
						/>,
					);
				}
			}
			if (hasLoomButton && !isLoomDisabled) {
				secondaryActionsTeam25.push(
					<PageHeaderLoomEntryPoint
						contentId={contentId}
						crossSellPopupPlacement="left-end"
						spaceKey={spaceKey ?? ''}
						contentType={contentType ?? ''}
						isCircle={!isRovoButtonEnabled}
						testId="sidebar-loom-button"
						source="contentWrapper"
						isDisabled={isNetworkDisabled}
					/>,
				);
			}
			if (editorAIActions && fg('platform_editor_ai_aifc_patch_beta')) {
				secondaryActionsTeam25.push(
					<EditorAIActions editorAIActions={editorAIActions} isDisabled={aiIsStreaming} />,
				);
			}
		} else {
			if (hasCommentsButton && fg('confluence-frontend-comments-panel')) {
				primaryActions.push(
					<CommentPanelButton contentId={contentId} isDisabled={isNetworkDisabled} />,
				);
				if (!sidebarIsExpanded) {
					collapsedActions.push(
						<CommentPanelButton contentId={contentId} collapsed isDisabled={isNetworkDisabled} />,
					);
				}
			}

			if (hasDetailsButton) {
				primaryActions.push(
					<DetailsPanelButton contentId={contentId} isDisabled={isNetworkDisabled} />,
				);
				if (!sidebarIsExpanded) {
					collapsedActions.push(
						<DetailsPanelButton contentId={contentId} collapsed isDisabled={isNetworkDisabled} />,
					);
				}
			}

			if (hasAudioButton) {
				if (fg('confluence_audio_briefings_v3')) {
					secondaryActions.push(
						<AudioListenButtonWrapper
							isRovoButtonEnabled={isRovoButtonEnabled}
							contentId={contentId}
							contentType={contentType ?? ''}
							spaceKey={spaceKey ?? ''}
							title={contentTitle ?? ''}
						/>,
					);
				} else {
					secondaryActions.push(
						<AudioButton
							contentId={contentId}
							contentType={contentType}
							spaceKey={spaceKey}
							contentTitle={contentTitle}
							isDisabled={isNetworkDisabled}
						/>,
					);
				}
			}

			if (hasLoomButton && !isLoomDisabled) {
				secondaryActions.push(
					<PageHeaderLoomEntryPoint
						contentId={contentId}
						crossSellPopupPlacement="left-end"
						spaceKey={spaceKey ?? ''}
						contentType={contentType ?? ''}
						isCircle={!isRovoButtonEnabled}
						testId="sidebar-loom-button"
						source="contentWrapper"
						isDisabled={isNetworkDisabled}
					/>,
				);
			}

			if (hasFeedbackButton) {
				secondaryActions.push(
					<FeedbackCollectorButton contentId={contentId} isDisabled={isNetworkDisabled} />,
				);
			}
		}

		if (isOnPDFExportPageRoute) {
			return null;
		}

		const globalSidebarMargin =
			pageMode === PageMode.VIEW && globalSidebarWidth ? globalSidebarWidth : 0;
		const previewPanelMargin =
			pageMode === PageMode.VIEW && previewPanelWidth ? previewPanelWidth : 0;
		const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();
		// For databases in staging area, always allow rendering (isFullScreenEmbedView is typically undefined)
		const shouldRenderWhenEmbedded = isEmbeddedConfluence
			? isDatabasesStagingArea || isFullScreenEmbedView
			: !isEmbeddedConfluence;

		// Remove <SpotlightManager> on top of `ObjectSidebarSpotlightTour`  when FG `cc_modernize_ga_changeboarding` is enabled
		// This will allow the `ModernizeChangeboarding` component to have combined spollight tour managed by the <SpotlightManager> in `PageLayout`
		// We still keep the <SpotlightManager> in place for existing Live Doc changeboarding EAP and Open beta users.
		// We will remove `SpotlightManager` in this file when FG `cc_modernize_ga_changeboarding` is rolled out
		const SpotlightTourWrapper = fg('cc_modernize_ga_changeboarding')
			? React.Fragment
			: SpotlightManager;

		// If there's no AI button, when we collapse the action bar, we need to render an expand icon within the
		// existing action bar container. If there is an AI button, hovering over it should render the expand icon
		const shouldRenderIntegratedExpandButton = !hasAiButton && !sidebarIsExpanded;
		// We always render the toggle component into the DOM, but we change its visibility based on:
		// If the action bar is expanded: show on hover (regardless of AI button)
		// If the action bar is collapsed: always show if AI button present, otherwise never show
		const shouldDisplayHoverToggle =
			(sidebarIsExpanded && actionBarIsHovered) || (hasAiButton && !sidebarIsExpanded);

		const shouldRenderAISmartButton = hasAiButton && shouldRenderWhenEmbedded && !isMobileApp;
		const isRovoMiniModalShown =
			shouldRenderAISmartButton &&
			isRovoButtonEnabled &&
			rovoExperienceMode === ConversationAssistantUIModes.MINI_MODAL;

		const handleMouseEnter = () => {
			setActionBarIsHovered(true);
		};

		const handleMouseLeave = () => {
			setActionBarIsHovered(false);
		};

		const onToggleClick = () => {
			toggleObjectSidebarControl();
			// need to make sure the hover (dis)appears after clicking it
			setActionBarIsHovered(!sidebarIsExpanded);
		};

		const toggleLabel = sidebarIsExpanded
			? formatMessage(i18n.collapseToggleLabel)
			: formatMessage(i18n.expandToggleLabel);

		const integratedExpandButton = (
			<Tooltip position="left" content={formatMessage(i18n.expandToggleLabel)} hideTooltipOnClick>
				<IconButton
					appearance="subtle"
					icon={(iconProps: any) => <ChevronUpIcon {...iconProps} size="small" />}
					label={toggleLabel}
					onClick={toggleObjectSidebarControl}
					shape="circle"
					testId="integrated-expand-button"
				/>
			</Tooltip>
		);

		return (
			<>
				<AISmartButtonStateHandler
					spaceKey={spaceKey ?? ''}
					contentId={contentId}
					contentType={contentType ?? ''}
					lastModifiedDate={lastModifiedDate}
					contentTitle={contentTitle ?? ''}
					isUnpublishedDraft={isUnpublishedDraft}
				/>
				<div
					css={[
						objectSidebarStyles,
						(contentType === 'page' || contentType === 'blogpost' || pageMode === PageMode.VIEW) &&
							objectSidebarPaddingStyles,
						contentType === 'database' && databaseObjectSidebarPaddingStyles,
						largeBottomPadding && largeBottomPaddingStyles,
						isRovoMiniModalShown && miniModalModeStyles,
						pageMode === PageMode.VIEW &&
							!isRovoMiniModalShown &&
							!fg('confluence_frontend_content_wrapper') &&
							viewModeStylesWithLegacyFooter,
						pageMode === PageMode.VIEW &&
							!isRovoMiniModalShown &&
							fg('confluence_frontend_content_wrapper') &&
							viewModeStylesWithNewFooter,
						!renderServerPlaceholder && sidebarControlState === 'hidden' && hiddenStyles,
					]}
					data-testid="object-sidebar-container"
					data-vc="object-sidebar-container"
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						marginRight: globalSidebarMargin + previewPanelMargin,
					}}
					// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
					onMouseEnter={handleMouseEnter}
					// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
					onMouseLeave={handleMouseLeave}
				>
					{primaryActions.length + secondaryActions.length + secondaryActionsTeam25.length > 0 &&
						shouldRenderWhenEmbedded &&
						!isMobileApp &&
						fg('confluence_frontend_content_wrapper') && (
							<SpotlightTourWrapper>
								<SpotlightTarget name={OBJECT_SIDEBAR_TOUR_TARGETS.OBJECT_SIDEBAR_CONTROL}>
									<div css={collapsibleContainerStyles}>
										<Tooltip position="left" content={toggleLabel} hideTooltipOnClick>
											<div
												css={collapsibleToggleStyles}
												style={{
													paddingBottom: sidebarIsExpanded ? `${token('space.100')}` : undefined,
													visibility: shouldDisplayHoverToggle ? 'visible' : 'hidden',
												}}
												data-testid="collapsible-hover-toggle"
											>
												<IconButton
													appearance="subtle"
													icon={(iconProps: any) =>
														sidebarIsExpanded ? (
															<ChevronDownIcon {...iconProps} size="small" />
														) : (
															<ChevronUpIcon {...iconProps} size="small" />
														)
													}
													label={toggleLabel}
													onClick={onToggleClick}
													shape="circle"
													testId="collapsible-hover-button"
												/>
											</div>
										</Tooltip>
										<SpotlightTarget name={OBJECT_SIDEBAR_SPOTLIGHT_TARGET}>
											<div>
												{sidebarIsExpanded || renderServerPlaceholder ? (
													<AnimationWrapper actionBarIsHovered={actionBarIsHovered}>
														<Flex
															xcss={
																isRovoButtonEnabled
																	? collapsibleSquircleObjectSidebarContainerStyles
																	: collapsibleObjectSidebarContainerStyles
															}
															direction="column"
															gap="space.100"
															justifyContent="center"
															alignItems="center"
															testId="object-sidebar-control"
														>
															{primaryActions}
															{fg('platform_editor_ai_aifc_streaming') ? (
																<>
																	{primaryActions.length > 0 &&
																		secondaryActionsTeam25.length > 0 && (
																			<Box xcss={dividerStyle} />
																		)}
																	{secondaryActionsTeam25}
																</>
															) : (
																<>
																	{secondaryActions.length > 0 && <Box xcss={dividerStyle} />}
																	{secondaryActions}
																</>
															)}
														</Flex>
													</AnimationWrapper>
												) : (
													<>{collapsedActions}</>
												)}
											</div>
										</SpotlightTarget>
										{shouldRenderIntegratedExpandButton ? (
											<Flex
												xcss={
													isRovoButtonEnabled
														? collapsibleSquircleObjectSidebarContainerStyles
														: collapsibleObjectSidebarContainerStyles
												}
												direction="column"
												gap="space.100"
												justifyContent="center"
												alignItems="center"
												testId="object-sidebar-control"
											>
												{integratedExpandButton}
											</Flex>
										) : null}
									</div>
								</SpotlightTarget>
								<SpotlightTransition>
									<ObjectSidebarSpotlightTour contentId={contentId} />
								</SpotlightTransition>
							</SpotlightTourWrapper>
						)}
					{shouldRenderAISmartButton &&
						(isRovoButtonEnabled ? (
							<div css={rovoButtonCollapsibleBottomSidebarStyles}>
								<AISmartButtonWrapper
									contentId={contentId}
									contentType={contentType ?? ''}
									pageMode={pageMode}
									isDisabled={isNetworkDisabled}
								/>
							</div>
						) : (
							<Box xcss={collapsibleBottomSidebarStyles}>
								<AIFloatingContextMenu
									spaceKey={spaceKey ?? ''}
									contentId={contentId}
									contentType={contentType ?? ''}
									lastModifiedDate={lastModifiedDate}
									pageCommentsSectionInView={pageCommentsSectionInView}
									isLivePage={isLivePage}
									isInObjectSideBar
								/>
							</Box>
						))}
				</div>
				<PageSegmentLoadEnd metric={OBJECT_SIDEBAR_CONTROL_METRIC} />
			</>
		);
	},
);

const AnimationWrapper = ({
	children,
	actionBarIsHovered,
}: {
	children: React.ReactNode;
	actionBarIsHovered: boolean;
}) => {
	if (expValEquals('cc-motion-survey-experiment', 'cohort', 'experiment')) {
		return (
			<AnimateIn
				duration="medium"
				enteringAnimation="fade-in-from-bottom"
				exitingAnimation="fade-out-from-top"
				animationTimingFunction="ease-in-40-out"
			>
				{(animateProps) => (
					<div
						{...(actionBarIsHovered ? animateProps : {})}
						data-testid="action-bar-animation-wrapper"
					>
						{children}
					</div>
				)}
			</AnimateIn>
		);
	}
	return <>{children}</>;
};
